import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from '../config/api';
import {
  CalendarBlank,
  Clock,
  MapPin,
  Phone,
  Envelope,
  CheckCircle,
  CircleNotch,
  CaretLeft,
  CaretRight,
  User,
  Wrench
} from '@phosphor-icons/react';

function ServiceCard({ svc, selected, onClick }) {
  return (
    <button
      onClick={() => onClick(svc.key)}
      className={`text-left p-4 rounded-xl border-2 transition-all ${
        selected ? 'border-blue-500 bg-blue-50 shadow-md' : 'border-slate-200 bg-white hover:border-slate-300'
      }`}
      data-testid={`service-${svc.key}`}
    >
      <p className="font-semibold text-slate-800 text-sm">{svc.label}</p>
      <p className="text-xs text-slate-500 mt-1">Duracion: ~{svc.duration_hours}h</p>
    </button>
  );
}

function TimeSlot({ time, selected, onClick, disabled }) {
  return (
    <button
      onClick={() => !disabled && onClick(time)}
      disabled={disabled}
      className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all border ${
        disabled
          ? 'bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed'
          : selected
            ? 'bg-blue-600 text-white border-blue-600 shadow-md'
            : 'bg-white text-slate-700 border-slate-200 hover:border-blue-400'
      }`}
      data-testid={`slot-${time}`}
    >
      {time}
    </button>
  );
}

export default function BookingPage() {
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState('');
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '', city: '', zip_code: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/api/booking/services`).then(r => setServices(r.data)).catch(() => {});
  }, []);

  // Set min date to tomorrow
  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 1);
  const minDateStr = minDate.toISOString().split('T')[0];

  useEffect(() => {
    if (!selectedDate) return;
    setLoadingSlots(true);
    setSelectedSlot('');
    axios.get(`${API_URL}/api/booking/available-slots?date=${selectedDate}&service=${selectedService}`)
      .then(r => setSlots(r.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [selectedDate, selectedService]);

  const handleSubmit = async () => {
    if (!form.name || !form.email || !form.phone) {
      setError('Nombre, email y telefono son requeridos');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/api/booking/book`, {
        ...form,
        service: selectedService,
        date: selectedDate,
        time_slot: selectedSlot
      });
      setSuccess(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear la reserva');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center" data-testid="booking-success">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={40} className="text-green-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Reserva Confirmada</h2>
          <p className="text-slate-500 mb-4">Te hemos enviado un correo con los detalles.</p>
          <div className="bg-slate-50 rounded-xl p-4 text-left text-sm space-y-2">
            <p><strong>Servicio:</strong> {services.find(s => s.key === selectedService)?.label}</p>
            <p><strong>Fecha:</strong> {selectedDate}</p>
            <p><strong>Hora:</strong> {selectedSlot}</p>
            <p><strong>Nombre:</strong> {form.name}</p>
          </div>
          <button
            onClick={() => { setSuccess(null); setStep(1); setSelectedService(''); setSelectedDate(''); setSelectedSlot(''); setForm({ name: '', email: '', phone: '', address: '', city: '', zip_code: '', notes: '' }); }}
            className="mt-6 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            data-testid="book-another"
          >
            Agendar otra cita
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-8 px-4" data-testid="booking-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Agendar Cita</h1>
          <p className="text-slate-500">IQES LLC - Low Voltage Solutions</p>
          <p className="text-sm text-slate-400 mt-1">Orlando, FL | 786-436-7307</p>
        </div>

        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map(s => (
            <div key={s} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                step >= s ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'
              }`}>{s}</div>
              {s < 3 && <div className={`w-12 h-0.5 ${step > s ? 'bg-blue-600' : 'bg-slate-200'}`} />}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
          {/* Step 1: Service */}
          {step === 1 && (
            <div data-testid="booking-step-1">
              <h2 className="text-lg font-bold text-slate-900 mb-1">Selecciona un servicio</h2>
              <p className="text-sm text-slate-500 mb-4">Elige el tipo de servicio que necesitas</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {services.map(svc => (
                  <ServiceCard key={svc.key} svc={svc} selected={selectedService === svc.key} onClick={setSelectedService} />
                ))}
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setStep(2)}
                  disabled={!selectedService}
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-40 inline-flex items-center gap-2"
                  data-testid="next-step-1"
                >
                  Siguiente <CaretRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Date & Time */}
          {step === 2 && (
            <div data-testid="booking-step-2">
              <h2 className="text-lg font-bold text-slate-900 mb-1">Selecciona fecha y hora</h2>
              <p className="text-sm text-slate-500 mb-4">Horario: Lunes a Viernes, 8:00 AM - 5:00 PM</p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 mb-1">Fecha</label>
                <input
                  type="date"
                  value={selectedDate}
                  min={minDateStr}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                  data-testid="booking-date"
                />
              </div>
              {loadingSlots ? (
                <div className="flex justify-center py-8"><CircleNotch size={32} className="animate-spin text-blue-600" /></div>
              ) : selectedDate && (
                <>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Horarios disponibles</label>
                  {slots.length > 0 ? (
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                      {slots.map(s => (
                        <TimeSlot key={s} time={s} selected={selectedSlot === s} onClick={setSelectedSlot} />
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">No hay horarios disponibles para esta fecha. Intenta otro dia.</p>
                  )}
                </>
              )}
              <div className="mt-6 flex justify-between">
                <button onClick={() => setStep(1)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg inline-flex items-center gap-1">
                  <CaretLeft size={16} /> Atras
                </button>
                <button
                  onClick={() => setStep(3)}
                  disabled={!selectedSlot}
                  className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-40 inline-flex items-center gap-2"
                  data-testid="next-step-2"
                >
                  Siguiente <CaretRight size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Contact Info */}
          {step === 3 && (
            <div data-testid="booking-step-3">
              <h2 className="text-lg font-bold text-slate-900 mb-1">Tu informacion</h2>
              <p className="text-sm text-slate-500 mb-4">Completa tus datos para confirmar la reserva</p>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Nombre completo *</label>
                  <div className="relative">
                    <User size={16} className="absolute left-3 top-3 text-slate-400" />
                    <input value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                      className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="Tu nombre" data-testid="booking-name" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Email *</label>
                    <div className="relative">
                      <Envelope size={16} className="absolute left-3 top-3 text-slate-400" />
                      <input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                        className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="tu@email.com" data-testid="booking-email" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Telefono *</label>
                    <div className="relative">
                      <Phone size={16} className="absolute left-3 top-3 text-slate-400" />
                      <input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                        className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="786-000-0000" data-testid="booking-phone" />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Direccion del servicio</label>
                  <div className="relative">
                    <MapPin size={16} className="absolute left-3 top-3 text-slate-400" />
                    <input value={form.address} onChange={e => setForm({...form, address: e.target.value})}
                      className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="Direccion" data-testid="booking-address" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <input value={form.city} onChange={e => setForm({...form, city: e.target.value})}
                    className="px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="Ciudad" data-testid="booking-city" />
                  <input value={form.zip_code} onChange={e => setForm({...form, zip_code: e.target.value})}
                    className="px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500" placeholder="Zip Code" data-testid="booking-zip" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Notas adicionales</label>
                  <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500" rows={2} placeholder="Detalles adicionales..." data-testid="booking-notes" />
                </div>
              </div>

              {error && <p className="text-sm text-red-600 bg-red-50 p-3 rounded-lg mt-3">{error}</p>}

              {/* Summary */}
              <div className="bg-slate-50 rounded-xl p-4 mt-4 text-sm space-y-1">
                <p className="font-semibold text-slate-700">Resumen:</p>
                <p><Wrench size={12} className="inline mr-1" /> {services.find(s => s.key === selectedService)?.label}</p>
                <p><CalendarBlank size={12} className="inline mr-1" /> {selectedDate} a las {selectedSlot}</p>
              </div>

              <div className="mt-6 flex justify-between">
                <button onClick={() => setStep(2)} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg inline-flex items-center gap-1">
                  <CaretLeft size={16} /> Atras
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting || !form.name || !form.email || !form.phone}
                  className="px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:opacity-40 inline-flex items-center gap-2"
                  data-testid="confirm-booking"
                >
                  {submitting ? <CircleNotch size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                  {submitting ? 'Reservando...' : 'Confirmar Reserva'}
                </button>
              </div>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">IQES LLC | 255 S Orange Avenue, Suite 104 #1905, Orlando, FL 32801</p>
      </div>
    </div>
  );
}
